import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * A4 referral history sheet with two modes:
 *   - "summary": one-page concise totals
 *   - "detailed": full list of every referred member + every referral bonus tx
 *
 * Props:
 *   member        — the loyalty member object (with name, membership_number, level, etc.)
 *   data          — referral-details API response (referred_count, referred_members, bonuses_received, etc.)
 *   mode          — "summary" | "detailed"
 *   approvedBy    — name of the admin who approved the print (optional)
 *   requestedBy   — name of the requester (optional)
 */
const ReferralHistorySheet = React.forwardRef(({ member, data, mode = 'summary', approvedBy, requestedBy }, ref) => {
  const loyaltyUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/loyalty`
      : 'https://kegentssaloon.com/loyalty';

  const fmtDate = (s) => {
    if (!s) return '-';
    try {
      return new Date(s).toLocaleDateString('en-GB');
    } catch {
      return s;
    }
  };
  const fmtAed = (n) => `${Number(n || 0).toFixed(2)} د.إ`;

  const referred = data?.referred_members || [];
  const bonusesReceived = data?.bonuses_received || [];
  const bonusesPending = data?.bonuses_pending || [];

  const isDetailed = mode === 'detailed';

  return (
    <div
      ref={ref}
      style={{
        padding: '12mm 14mm',
        fontFamily: 'Cairo, Tajawal, Arial, sans-serif',
        background: '#fff',
        color: '#111',
        width: '210mm',
        minHeight: '297mm',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
      dir="rtl"
    >
      {/* Header */}
      <div
        dir="ltr"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          gap: 14,
          marginBottom: 12,
          borderBottom: '3px double #d4af37',
          paddingBottom: 10,
        }}
      >
        <div style={{ textAlign: 'left' }} dir="ltr">
          <div style={{ fontSize: 11, color: '#666' }}>Confidential — Internal Use Only</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>KE Gents Saloon</div>
          <div style={{ fontSize: 11, color: '#666' }}>Referral History — {isDetailed ? 'Detailed' : 'Summary'}</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src="/logo-gold.png"
            alt="KE Gents Saloon"
            style={{ height: 80, width: 'auto', display: 'block' }}
            crossOrigin="anonymous"
          />
          <div
            style={{
              padding: 5,
              background: '#fff',
              border: '2px solid #d4af37',
              borderRadius: 6,
              display: 'inline-block',
              lineHeight: 0,
            }}
          >
            <QRCodeSVG value={loyaltyUrl} size={80} bgColor="#ffffff" fgColor="#111111" level="M" marginSize={0} />
          </div>
        </div>

        <div style={{ textAlign: 'right' }} dir="rtl">
          <div style={{ fontSize: 11, color: '#666' }}>سرّي — للاستخدام الداخلي فقط</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>KE Gents Saloon</div>
          <div style={{ fontSize: 11, color: '#666' }}>سجل الإحالة — {isDetailed ? 'مفصّل' : 'ملخّص'}</div>
        </div>
      </div>

      {/* Member identity card */}
      <div
        style={{
          background: 'linear-gradient(90deg, #fef3c7, transparent)',
          border: '1.5px solid #d4af37',
          borderRadius: 10,
          padding: '10px 14px',
          marginBottom: 12,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 12,
          fontSize: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 10, color: '#666' }}>الاسم / Name</div>
          <div style={{ fontWeight: 800, fontSize: 14 }}>{member?.name}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: '#666' }}>رقم العضوية / Membership #</div>
          <div style={{ fontWeight: 800, fontSize: 14, fontFamily: 'monospace' }}>
            #{member?.membership_number}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: '#666' }}>المستوى / Level</div>
          <div style={{ fontWeight: 800, fontSize: 14, color: member?.level?.color || '#111' }}>
            {member?.level?.name_ar} / {member?.level?.name_en}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 10,
          marginBottom: 14,
        }}
      >
        {[
          {
            label_ar: 'إجمالي الإحالات',
            label_en: 'Total Referrals',
            value: data?.referred_count || 0,
            color: '#3b82f6',
          },
          {
            label_ar: 'أتمّوا 3 زيارات',
            label_en: 'Completed 3 Visits',
            value: data?.referred_count_completed || 0,
            color: '#10b981',
          },
          {
            label_ar: 'نقاط مُعتمدة',
            label_en: 'Points Earned',
            value: data?.total_referral_points_earned || 0,
            color: '#d4af37',
            suffix: ' نقطة',
          },
          {
            label_ar: 'نقاط معلّقة',
            label_en: 'Points Pending',
            value: data?.total_referral_points_pending || 0,
            color: '#f59e0b',
            suffix: ' نقطة',
          },
        ].map((s, i) => (
          <div
            key={i}
            style={{
              border: `2px solid ${s.color}55`,
              borderRadius: 8,
              padding: '8px 10px',
              background: `${s.color}08`,
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 9, color: '#666' }}>{s.label_ar}</div>
            <div style={{ fontSize: 9, color: '#888' }}>{s.label_en}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: s.color, marginTop: 4 }}>
              {s.value}
              {s.suffix && <span style={{ fontSize: 11, marginInlineStart: 3 }}>{s.suffix}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Referred members list */}
      <div style={{ marginBottom: 12 }}>
        <h3
          style={{
            fontSize: 14,
            fontWeight: 800,
            margin: '0 0 6px 0',
            borderBottom: '2px solid #d4af37',
            paddingBottom: 3,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <span>👥 الأعضاء المُحالون ({referred.length})</span>
          <span dir="ltr" style={{ color: '#666', fontWeight: 600, fontSize: 11 }}>
            Referred members
          </span>
        </h3>
        {referred.length === 0 ? (
          <div style={{ fontSize: 11, color: '#999', textAlign: 'center', padding: 20 }}>
            لا توجد إحالات / No referrals yet
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: 10.5, borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f5f5f5' }}>
              <tr>
                <th style={{ textAlign: 'right', padding: 4, borderBottom: '1px solid #ddd' }}>الاسم / Name</th>
                <th style={{ textAlign: 'center', padding: 4, borderBottom: '1px solid #ddd' }}>#</th>
                <th style={{ textAlign: 'center', padding: 4, borderBottom: '1px solid #ddd' }}>تاريخ الانضمام / Joined</th>
                <th style={{ textAlign: 'center', padding: 4, borderBottom: '1px solid #ddd' }}>زيارات / Visits</th>
                <th style={{ textAlign: 'center', padding: 4, borderBottom: '1px solid #ddd' }}>المكافأة / Bonus</th>
              </tr>
            </thead>
            <tbody>
              {(isDetailed ? referred : referred.slice(0, 12)).map((r, idx) => (
                <tr key={r.id || idx} style={{ borderBottom: '1px dotted #eee' }}>
                  <td style={{ padding: 4, fontWeight: 600 }}>{r.name}</td>
                  <td style={{ padding: 4, textAlign: 'center', fontFamily: 'monospace' }}>#{r.membership_number}</td>
                  <td style={{ padding: 4, textAlign: 'center', color: '#666' }}>{fmtDate(r.joined_at)}</td>
                  <td style={{ padding: 4, textAlign: 'center', color: '#666' }}>{r.consecutive_visits_count || 0}</td>
                  <td style={{ padding: 4, textAlign: 'center' }}>
                    {r.referral_bonus_triggered ? (
                      <span style={{ color: '#10b981', fontWeight: 700 }}>✓ مكتملة</span>
                    ) : (
                      <span style={{ color: '#f59e0b' }}>قيد الإكمال</span>
                    )}
                  </td>
                </tr>
              ))}
              {!isDetailed && referred.length > 12 && (
                <tr>
                  <td colSpan={5} style={{ padding: 6, textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                    + {referred.length - 12} عضو إضافي (لرؤية الكل اطبع النسخة المفصّلة)
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Bonuses (only in detailed mode) */}
      {isDetailed && (
        <div style={{ marginBottom: 12 }}>
          <h3
            style={{
              fontSize: 14,
              fontWeight: 800,
              margin: '0 0 6px 0',
              borderBottom: '2px solid #d4af37',
              paddingBottom: 3,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>💰 سجل المكافآت ({bonusesReceived.length + bonusesPending.length})</span>
            <span dir="ltr" style={{ color: '#666', fontWeight: 600, fontSize: 11 }}>
              Bonus history
            </span>
          </h3>
          {bonusesReceived.length + bonusesPending.length === 0 ? (
            <div style={{ fontSize: 11, color: '#999', textAlign: 'center', padding: 16 }}>
              لا توجد مكافآت بعد
            </div>
          ) : (
            <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f5f5f5' }}>
                <tr>
                  <th style={{ textAlign: 'right', padding: 3, borderBottom: '1px solid #ddd' }}>التاريخ</th>
                  <th style={{ textAlign: 'center', padding: 3, borderBottom: '1px solid #ddd' }}>النقاط</th>
                  <th style={{ textAlign: 'center', padding: 3, borderBottom: '1px solid #ddd' }}>الحالة</th>
                  <th style={{ textAlign: 'right', padding: 3, borderBottom: '1px solid #ddd' }}>السبب</th>
                </tr>
              </thead>
              <tbody>
                {[...bonusesReceived, ...bonusesPending].map((b, idx) => (
                  <tr key={b.id || idx} style={{ borderBottom: '1px dotted #eee' }}>
                    <td style={{ padding: 3, color: '#666' }}>{fmtDate(b.requested_at || b.decided_at)}</td>
                    <td style={{ padding: 3, textAlign: 'center', fontWeight: 700, color: '#10b981' }}>+{b.points}</td>
                    <td style={{ padding: 3, textAlign: 'center' }}>
                      {b.status === 'approved' && <span style={{ color: '#10b981' }}>معتمد</span>}
                      {b.status === 'pending' && <span style={{ color: '#f59e0b' }}>قيد الاعتماد</span>}
                    </td>
                    <td style={{ padding: 3, fontSize: 9.5 }}>{b.reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          marginTop: 'auto',
          paddingTop: 8,
          borderTop: '2px solid #d4af37',
          textAlign: 'center',
          fontSize: 9,
          color: '#666',
        }}
      >
        <div style={{ fontWeight: 600 }}>
          KE Gents Saloon — {loyaltyUrl.replace(/^https?:\/\//, '')} • {fmtAed(0).replace('0.00', '')}
        </div>
        <div style={{ marginTop: 2 }}>
          {`طُبع في / Printed: ${new Date().toLocaleString('en-GB')}`}
          {requestedBy && ` • طلب من: ${requestedBy}`}
          {approvedBy && ` • اعتمد من: ${approvedBy}`}
        </div>
      </div>
    </div>
  );
});
ReferralHistorySheet.displayName = 'ReferralHistorySheet';

export default ReferralHistorySheet;
